import { useCallback, useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react'
import { ADMIN_STEP_MINUTES, conflictsFor, hasStarted, isActive, nowMinutes, outsideHours, type Conflict } from '../../core/rules/agenda'
import { toTime } from '../../core/rules/schedule'
import { today } from '../../core/format/dates'
import { PX_PER_MINUTE } from './agendaRules'
import type { OpeningDay } from '../../core/types/site'
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda'

/** Waar een gesleepte afspraak zou landen, met wat dat betekent. */
export type DragTarget = {
  id: string
  date: string
  time: string
  conflicts: Conflict[]
  outside: boolean
  /** Te vroeg gesleept (vóór nu): vastgeklikt op het eerste kwartier na nu. */
  snappedToNow: boolean
}

type Options = {
  start: number
  end: number
  hours: OpeningDay[]
  appointments: AgendaAppointment[]
  blocks: AgendaBlock[]
  now: Date
  enabled: boolean
  onDrop: (target: DragTarget) => void
}

type Grab = {
  a: AgendaAppointment
  pointerId: number
  touch: boolean
  x0: number
  y0: number
  /** Laatste vingerpositie: nodig om bij automatisch scrollen opnieuw te rekenen. */
  x: number
  y: number
  offset: number
  active: boolean
  /** Touch: de timer van het lang indrukken. */
  timer: number | null
}

/** Muis/pen: pas na zoveel pixels is het slepen; minder is gewoon een klik (afspraak openen). */
const DRAG_THRESHOLD_PX = 5
/** Touch: zo lang stil indrukken tilt de afspraak op (korter dan het contextmenu van Android, ±500 ms). */
export const LONG_PRESS_MS = 380
/** Touch: beweegt de vinger meer dan dit vóór het optillen, dan is het scrollen, geen slepen. */
const TOUCH_SLOP_PX = 10
/** Bij slepen dicht bij de rand van het scherm scrolt de pagina mee (boven: kop, onder: tabbalk). */
const EDGE_TOP_PX = 96
const EDGE_BOTTOM_PX = 140
const EDGE_SPEED_PX = 10

const clearTimer = (g: Grab | null) => {
  if (g?.timer != null) window.clearTimeout(g.timer)
  if (g) g.timer = null
}
const stopFrame = (frame: { current: number | null }) => {
  if (frame.current !== null) cancelAnimationFrame(frame.current)
  frame.current = null
}

/**
 * Afspraken verslepen op de tijdlijn. Klikt vast op kwartieren, kan niet het verleden in (dan het
 * eerste kwartier na nu), en meldt botsingen met dezelfde regels als het formulier. Er wordt pas
 * iets verzet na bevestigen: de klant krijgt er bericht van.
 *
 * - Muis en pen: indrukken en slepen.
 * - Touch: lang indrukken tilt de afspraak op (trilt kort), dan slepen. Vegen zonder eerst stil te
 *   houden blijft gewoon scrollen; zolang de afspraak is opgetild scrolt de pagina niet mee, behalve
 *   automatisch aan de rand van het scherm.
 */
export function useTimelineDrag(opts: Options) {
  const columns = useRef(new Map<string, HTMLDivElement>())
  const grab = useRef<Grab | null>(null)
  const suppressClick = useRef(false)
  const latest = useRef(opts)
  latest.current = opts
  const [target, setTarget] = useState<DragTarget | null>(null)
  const shown = useRef<DragTarget | null>(null)
  const scrollFrame = useRef<number | null>(null)

  const targetAt = useCallback((a: AgendaAppointment, x: number, y: number, offset: number): DragTarget | null => {
    const o = latest.current
    let date: string | null = null
    let rect: DOMRect | null = null
    let best = Infinity
    for (const [d, el] of columns.current) {
      const r = el.getBoundingClientRect()
      const dist = x < r.left ? r.left - x : x > r.right ? x - r.right : 0
      if (dist < best) {
        best = dist
        date = d
        rect = r
      }
    }
    if (!date || !rect || date < today()) return null

    const latestStart = o.end - a.durationMinutes
    let minutes = Math.round((o.start + (y - rect.top) / PX_PER_MINUTE - offset) / ADMIN_STEP_MINUTES) * ADMIN_STEP_MINUTES
    minutes = Math.max(o.start, Math.min(minutes, latestStart))
    let snappedToNow = false
    const nowAt = nowMinutes(date, o.now)
    if (nowAt !== null) {
      const earliest = Math.ceil((nowAt + 1) / ADMIN_STEP_MINUTES) * ADMIN_STEP_MINUTES
      if (earliest > latestStart) return null
      if (minutes < earliest) {
        minutes = earliest
        snappedToNow = true
      }
    }
    const time = toTime(minutes)
    const span = { date, time, durationMinutes: a.durationMinutes, staffId: a.staffId }
    return {
      id: a.id,
      date,
      time,
      conflicts: conflictsFor(span, o.appointments, o.blocks, { ignoreId: a.id }),
      outside: outsideHours(o.hours, span),
      snappedToNow,
    }
  }, [])

  /** Nieuwe stand alleen als het kwartier of de dag verandert: geen render per pixel. */
  const show = useCallback(
    (g: Grab) => {
      // Buiten bereik (bijv. een dag in het verleden): de laatste geldige plek blijft staan.
      const next = targetAt(g.a, g.x, g.y, g.offset) ?? shown.current
      if (!next) return
      if (shown.current && shown.current.date === next.date && shown.current.time === next.time) return
      shown.current = next
      setTarget(next)
    },
    [targetAt],
  )

  /** Aan de rand van het scherm: de pagina schuift mee, en de landingsplek rekent mee. Eén frame per keer. */
  const edgeScroll = () => {
    const step = () => {
      const g = grab.current
      const dir = !g?.active ? 0 : g.y < EDGE_TOP_PX ? -1 : g.y > window.innerHeight - EDGE_BOTTOM_PX ? 1 : 0
      if (!g || dir === 0) return stopFrame(scrollFrame)
      window.scrollBy(0, dir * EDGE_SPEED_PX)
      show(g)
      scrollFrame.current = requestAnimationFrame(step)
    }
    if (scrollFrame.current === null) scrollFrame.current = requestAnimationFrame(step)
  }

  const cancel = useCallback(() => {
    if (grab.current?.active) suppressClick.current = true
    clearTimer(grab.current)
    stopFrame(scrollFrame)
    grab.current = null
    shown.current = null
    setTarget(null)
  }, [])

  // Escape breekt het slepen af. In de capture-fase, zodat het zijpaneel niet ook sluit.
  useEffect(() => {
    if (!target) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      cancel()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [target, cancel])

  // Touch: zolang er een vinger op een afspraak staat, beslissen we per beweging. Opgetild = niet
  // scrollen (preventDefault, daarom niet-passief); nog niet opgetild en te ver bewogen = scrollen.
  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      const g = grab.current
      if (!g?.touch) return
      if (g.active) {
        if (e.cancelable) e.preventDefault()
        return
      }
      const t = e.touches[0]
      if (t && Math.hypot(t.clientX - g.x0, t.clientY - g.y0) > TOUCH_SLOP_PX) {
        clearTimer(g)
        grab.current = null
      }
    }
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    const frame = scrollFrame
    return () => {
      window.removeEventListener('touchmove', onTouchMove)
      clearTimer(grab.current)
      stopFrame(frame)
    }
  }, [])

  const canDrag = (a: AgendaAppointment) => latest.current.enabled && isActive(a) && !hasStarted(a, latest.current.now)

  const lift = (g: Grab) => {
    g.active = true
    g.timer = null
    navigator.vibrate?.(12)
    show(g)
  }

  const handlers = (a: AgendaAppointment) => ({
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0 || !canDrag(a)) return
      const r = e.currentTarget.getBoundingClientRect()
      const touch = e.pointerType === 'touch'
      const g: Grab = {
        a,
        pointerId: e.pointerId,
        touch,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        offset: (e.clientY - r.top) / PX_PER_MINUTE,
        active: false,
        timer: null,
      }
      grab.current = g
      if (touch) {
        // Touch-pointers zijn al aan het element gebonden; optillen pas na lang indrukken.
        g.timer = window.setTimeout(() => {
          if (grab.current === g) lift(g)
        }, LONG_PRESS_MS)
      } else {
        e.currentTarget.setPointerCapture(e.pointerId)
      }
    },
    onPointerMove: (e: PointerEvent<HTMLButtonElement>) => {
      const g = grab.current
      if (!g || g.pointerId !== e.pointerId) return
      g.x = e.clientX
      g.y = e.clientY
      if (!g.active) {
        // Touch tilt alleen op via de timer; muis/pen na een paar pixels.
        if (g.touch || Math.hypot(e.clientX - g.x0, e.clientY - g.y0) < DRAG_THRESHOLD_PX) return
        g.active = true
      }
      show(g)
      edgeScroll()
    },
    onPointerUp: (e: PointerEvent<HTMLButtonElement>) => {
      const g = grab.current
      if (!g || g.pointerId !== e.pointerId) return
      clearTimer(g)
      stopFrame(scrollFrame)
      grab.current = null
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
      if (!g.active) return
      suppressClick.current = true
      const drop = targetAt(g.a, e.clientX, e.clientY, g.offset) ?? shown.current
      shown.current = null
      setTarget(null)
      if (drop && (drop.date !== g.a.date || drop.time !== g.a.time)) latest.current.onDrop(drop)
    },
    // De browser neemt het over (scrollen begon toch): niets verzetten.
    onPointerCancel: () => cancel(),
    // Lang indrukken op touch geeft anders het contextmenu of tekstselectie.
    onContextMenu: (e: MouseEvent<HTMLButtonElement>) => {
      if (grab.current?.touch) e.preventDefault()
    },
    // Na slepen is de "klik" van het loslaten geen klik: niet de afspraak openen.
    onClickCapture: (e: MouseEvent<HTMLButtonElement>) => {
      if (!suppressClick.current) return
      suppressClick.current = false
      e.preventDefault()
      e.stopPropagation()
    },
  })

  const registerColumn = (date: string) => (el: HTMLDivElement | null) => {
    if (el) columns.current.set(date, el)
    else columns.current.delete(date)
  }

  return { target, dragging: target !== null, handlers, canDrag, registerColumn }
}
